// Site Core - Global Utilities and Functions

/**
 * Toast Notification System
 * A centralized toast notification utility supporting multiple types and auto-dismiss
 */
window.Toast = {
    /**
     * Show a toast notification
     * @param {string} message - The message to display
     * @param {string} type - Toast type: 'success', 'error', 'warning', 'info', 'danger'
     * @param {number} duration - Duration in milliseconds (default: 3000)
     * @param {object} options - Additional options
     */
    show(message, type = 'info', duration = 3000, options = {}) {
        const defaultOptions = {
            position: 'top-end', // top-start, top-center, top-end, bottom-start, bottom-center, bottom-end
            dismissible: true,
            icon: true,
            animation: true
        };

        const config = { ...defaultOptions, ...options };

        // Create toast container if it doesn't exist
        let container = document.getElementById('toast-container');
        if (!container) {
            container = document.createElement('div');
            container.id = 'toast-container';
            container.className = `toast-container position-fixed ${this.getPositionClass(config.position)}`;
            document.body.appendChild(container);
        }

        // Create toast element
        const toast = document.createElement('div');
        toast.className = `toast fade ${config.animation ? 'show' : ''}`;
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        toast.setAttribute('aria-atomic', 'true');

        // Get icon and color based on type
        const { icon, bgClass, textClass } = this.getTypeStyles(type);

        // Build toast HTML
        toast.innerHTML = `
            <div class="toast-header bg-${bgClass} text-${textClass}">
                ${config.icon && icon ? `<i class="${icon} me-2"></i>` : ''}
                <strong class="me-auto">${this.getTypeTitle(type)}</strong>
                ${config.dismissible ? '<button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast" aria-label="Close"></button>' : ''}
            </div>
            <div class="toast-body">
                ${message}
            </div>
        `;

        // Add toast to container
        container.appendChild(toast);

        // Initialize Bootstrap toast
        const bsToast = new bootstrap.Toast(toast, {
            autohide: duration > 0,
            delay: duration
        });

        bsToast.show();

        // Remove toast after it's hidden
        toast.addEventListener('hidden.bs.toast', () => {
            toast.remove();
            
            // Remove container if empty
            if (container.children.length === 0) {
                container.remove();
            }
        });

        return toast;
    },

    /**
     * Show success toast
     */
    success(message, duration = 3000, options = {}) {
        return this.show(message, 'success', duration, options);
    },

    /**
     * Show error toast
     */
    error(message, duration = 4000, options = {}) {
        return this.show(message, 'error', duration, options);
    },

    /**
     * Show warning toast
     */
    warning(message, duration = 3500, options = {}) {
        return this.show(message, 'warning', duration, options);
    },

    /**
     * Show info toast
     */
    info(message, duration = 3000, options = {}) {
        return this.show(message, 'info', duration, options);
    },

    /**
     * Show danger toast (alias for error)
     */
    danger(message, duration = 4000, options = {}) {
        return this.show(message, 'danger', duration, options);
    },

    /**
     * Get position class for container
     */
    getPositionClass(position) {
        const positions = {
            'top-start': 'top-0 start-0 p-3',
            'top-center': 'top-0 start-50 translate-middle-x p-3',
            'top-end': 'top-0 end-0 p-3',
            'bottom-start': 'bottom-0 start-0 p-3',
            'bottom-center': 'bottom-0 start-50 translate-middle-x p-3',
            'bottom-end': 'bottom-0 end-0 p-3'
        };

        return positions[position] || positions['top-end'];
    },

    /**
     * Get type-specific styles
     */
    getTypeStyles(type) {
        const styles = {
            success: {
                icon: 'bi bi-check-circle-fill',
                bgClass: 'success',
                textClass: 'white'
            },
            error: {
                icon: 'bi bi-exclamation-triangle-fill',
                bgClass: 'danger',
                textClass: 'white'
            },
            danger: {
                icon: 'bi bi-exclamation-triangle-fill',
                bgClass: 'danger',
                textClass: 'white'
            },
            warning: {
                icon: 'bi bi-exclamation-circle-fill',
                bgClass: 'warning',
                textClass: 'dark'
            },
            info: {
                icon: 'bi bi-info-circle-fill',
                bgClass: 'info',
                textClass: 'white'
            }
        };

        return styles[type] || styles.info;
    },

    /**
     * Get type-specific title
     */
    getTypeTitle(type) {
        const titles = {
            success: 'Success',
            error: 'Error',
            danger: 'Error',
            warning: 'Warning',
            info: 'Information'
        };

        return titles[type] || 'Notification';
    }
};

/**
 * Alias for backward compatibility
 */
window.showToast = (message, type, duration) => {
    return Toast.show(message, type, duration);
};
